/**
 * Copyright (c) 2025-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { gql, type TypedDocumentNode } from "@apollo/client";
import { skipToken, useSuspenseQuery } from "@apollo/client/react";
import { type ReactNode, Suspense, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import config from "../../../config";
import type {
  GQLArticleStep_LearningpathStepFragment,
  GQLLearningpathStepQuery,
  GQLLearningpathStepQueryVariables,
} from "../../../graphqlTypes";
import type { Breadcrumb } from "../../../interfaces";
import { getArticleScripts } from "../../../util/getArticleScripts";
import { structuredArticleDataFragment } from "../../../util/getStructuredDataFromArticle";
import { useRawLocation } from "../../../util/localePath";
import { transformArticle } from "../../../util/transformArticle";
import { Article } from "../../Article/Article";
import { CreatedBy } from "../../Article/CreatedBy";
import { ContentPlaceholder } from "../../ContentPlaceholder";
import { DefaultErrorMessage } from "../../DefaultErrorMessage";
import { LdJson } from "../../LdJson";
import { ResourceContent } from "../../Resource/ResourceLayout";

interface ArticleStepProps {
  learningpathStep: GQLArticleStep_LearningpathStepFragment;
  breadcrumbItems: Breadcrumb[];
  subjectId?: string;
  taxId?: string;
  articleId?: string;
  children?: ReactNode;
  isInactive?: boolean;
  skipToContentId?: string;
}

export const ArticleStep = (props: ArticleStepProps) => (
  <Suspense
    fallback={
      <ResourceContent>
        <ContentPlaceholder variant="article" />
      </ResourceContent>
    }
  >
    <ArticleStepContent {...props} />
  </Suspense>
);

const ArticleStepContent = ({
  learningpathStep,
  skipToContentId,
  subjectId,
  breadcrumbItems,
  articleId,
  taxId,
  children,
  isInactive,
}: ArticleStepProps) => {
  const { t, i18n } = useTranslation();
  const location = useRawLocation();

  const stepQuery = useSuspenseQuery(
    learningpathStepQuery,
    !!learningpathStep.resource?.article || !articleId || (!learningpathStep.embedUrl && !learningpathStep.resource)
      ? skipToken
      : {
          variables: {
            articleId,
            resourceId: taxId ?? "",
            includeResource: !!taxId,
            transformArgs: {
              path: location.pathname,
              subjectId,
            },
          },
        },
  );

  const url = !learningpathStep.resource?.url ? stepQuery.data?.node?.url : undefined;
  const contentUrl = url ? `${config.ndlaFrontendDomain}${url}` : undefined;

  useEffect(() => {
    if (window.MathJax && typeof window.MathJax.typesetPromise === "function") {
      try {
        window.MathJax.typesetPromise();
      } catch {
        // do nothing
      }
    }
  });

  const [article, scripts] = useMemo(() => {
    const article = learningpathStep.resource?.article ? learningpathStep.resource.article : stepQuery.data?.article;
    if (!article) return [undefined, undefined];
    return [
      transformArticle(article, i18n.language, {
        path: `${config.ndlaFrontendDomain}/article/${article.id}`,
        subject: subjectId,
        articleLanguage: article.language,
      }),
      getArticleScripts(article.requiredLibraries, article.transformedContent.content, i18n.language),
    ];
  }, [i18n.language, learningpathStep?.resource?.article, stepQuery.data?.article, subjectId]);

  if (!article || !scripts) {
    return null;
  }

  const learningpathStepResource = learningpathStep.resource ?? stepQuery.data;
  const resource = learningpathStep.resource ?? stepQuery.data?.node;
  const stepArticle = learningpathStepResource?.article;

  if (!stepArticle) {
    return <DefaultErrorMessage applySkipToContentId={!!skipToContentId} />;
  }

  return (
    <ResourceContent variant="content">
      {!!article?.metaDescription && <meta name="description" content={article.metaDescription} />}
      {scripts.map((script) => (
        <script key={script.src} src={script.src} type={script.type} async={script.async} defer={script.defer} />
      ))}
      <LdJson article={stepArticle} breadcrumbItems={breadcrumbItems} />
      <Article
        id={skipToContentId}
        article={article}
        resourceTypes={resource?.resourceTypes}
        relevanceId={resource?.relevanceId}
        isInactive={isInactive}
      >
        {children}
        {!!url && <CreatedBy name={t("createdBy.content")} description={t("createdBy.text")} url={contentUrl} />}
      </Article>
    </ResourceContent>
  );
};

export const articleFragment = gql`
  fragment LearningpathEmbed_Article on Article {
    id
    metaDescription
    created
    updated
    articleType
    requiredLibraries {
      name
      url
      mediaType
    }
    ...StructuredArticleData
    ...Article_Article
  }
  ${structuredArticleDataFragment}
  ${Article.fragments.article}
`;

ArticleStep.fragments = {
  article: articleFragment,
  learningpathStep: gql`
    fragment ArticleStep_LearningpathStep on BaseLearningpathStep {
      id
      title
      description
      introduction
      opengraph {
        title
        description
        url
      }
      resource {
        id
        nodeType
        url
        relevanceId
        resourceTypes {
          id
          name
        }
        article {
          ...LearningpathEmbed_Article
        }
      }
      embedUrl {
        embedType
        url
      }
      oembed {
        html
        width
        height
        type
        version
      }
    }
    ${articleFragment}
    ${structuredArticleDataFragment}
    ${Article.fragments.article}
  `,
};

const learningpathStepQuery: TypedDocumentNode<GQLLearningpathStepQuery, GQLLearningpathStepQueryVariables> = gql`
  query learningpathStep(
    $articleId: String!
    $resourceId: String!
    $includeResource: Boolean!
    $transformArgs: TransformedArticleContentInput
  ) {
    article(id: $articleId) {
      oembed
      ...LearningpathEmbed_Article
    }
    node(id: $resourceId) @include(if: $includeResource) {
      id
      nodeType
      url
      relevanceId
      resourceTypes {
        id
        name
      }
    }
  }
  ${articleFragment}
`;
